<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { useGoogleAnalytics, useValidation, useValidators } from '@/shared/composables'
  import { useCommonStore } from '@/shared/store'
  import { computed, onMounted, ref } from 'vue'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import { CONTACTS_KEYS, type Locale } from '@/shared/types/common.types'
  import { QA_PHONE_CODE } from './NeedHelpModalQC.constants'
  import ModalQC from '@qicgroupdoha/qic-connect-ds/components/ModalQC'
  import { FlowPrivacyPolicyReminderQC } from '@/entities/Flow/components/FlowPrivacyPolicyReminderQC'
  import WhatsAppButtonQC from '@/entities/Flow/components/WhatsAppButtonQC/WhatsAppButtonQC.vue'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import PhoneInputQC from '@qicgroupdoha/qic-connect-ds/components/PhoneInputQC'
  import HotlineButtonQC from '@/entities/Flow/components/HotlineButtonQC/HotlineButtonQC.vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'

  interface Props {
    page?: AvailableFlowRoutes
    dataTest?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    page: undefined,
    dataTest: 'need-help-modal',
  })

  const emit = defineEmits<Emits>()
  const { locale } = useI18n()

  interface Emits {
    (e: 'close'): void
  }

  const gaService = useGoogleAnalytics()
  const commonStore = useCommonStore()
  const phone = ref('')
  const maskedPhone = ref('')
  const phoneCompleted = ref(false)
  const callbackSent = ref(false)
  const isCallbackSendingProgress = ref(false)
  const isError = ref(false)
  const modalRef = ref<InstanceType<typeof ModalQC> | null>(null)
  const countryCode = ref(QA_PHONE_CODE)
  const { phoneValidator, required } = useValidators()
  const validationRules = {
    phone: {
      required,
    },
    phoneCompleted: {
      ...phoneValidator,
    },
  }
  const { v$, errors } = useValidation(validationRules, { phone, phoneCompleted })

  const onPhoneChange = (data: { unmasked: string; value: string; code: string }) => {
    if (phoneCompleted.value) {
      phone.value = `${data.code}${data.unmasked}`
    }
  }

  const sendCallback = async () => {
    v$.value.$touch()
    if (v$.value.$error || isCallbackSendingProgress.value) return
    try {
      isCallbackSendingProgress.value = true
      await commonStore.sendCallback(phone.value)
      if (props.page) {
        gaService.sendClickPhone(props.page, phone.value)
      }
      callbackSent.value = true
      isError.value = false
    } catch (err) {
      console.error(err)
      isError.value = true
    } finally {
      isCallbackSendingProgress.value = false
    }
  }

  const iconSrc = computed(() =>
    isError.value
      ? new URL('@/shared/assets/images/network-error.png', import.meta.url).href
      : new URL('@/shared/assets/images/insurance-result.png', import.meta.url).href
  )

  const onClose = () => {
    callbackSent.value = false
    isCallbackSendingProgress.value = false
    modalRef.value?.close()
  }

  const sendWhatsAppEvent = () => (props.page ? gaService.sendWhatsApp(props.page) : null)

  const contacts = computed(() => ({
    phoneLink: commonStore.getContactValue(CONTACTS_KEYS.PHONE_LINK),
    phoneText: commonStore.getContactValue(CONTACTS_KEYS.PHONE_LINK_TEXT),
    waLink: commonStore.getContactValue(CONTACTS_KEYS.WA_LINK_GENERAL),
  }))

  onMounted(async () => {
    await commonStore.getContacts()
  })
</script>

<template>
  <ModalQC
    ref="modalRef"
    :data-test="dataTest"
    :locale="locale as Locale"
    hide-controls
    @close="emit('close')"
  >
    <template #content>
      <div class="need-help-qc">
        <WrapperQC v-if="!callbackSent && !isError" direction="column">
          <WrapperQC direction="column" gap="var(--spacing-5)">
            <h3 class="need-help-qc__title heading-4 text-center">
              {{ $t('common.get_in_touch') }}
            </h3>
            <p class="text-small-medium text-center">{{ $t('common.availabilty_hours') }}</p>
          </WrapperQC>

          <WrapperQC
            direction="column"
            gap="var(--spacing-5)"
            :padding="{
              block: 'var(--spacing-5)',
            }"
          >
            <WhatsAppButtonQC
              :href="contacts.waLink"
              :loading="!contacts.waLink"
              data-test="contact-whatsapp-button"
              @on-click="sendWhatsAppEvent"
            />

            <HotlineButtonQC
              :loading="!contacts.phoneLink && !contacts.phoneText"
              :phone="contacts.phoneText"
              :link="contacts.phoneLink"
            />
          </WrapperQC>

          <div class="need-help-qc__or-border text-center">
            {{ $t('travel.common.get_in_touch.or') }}
          </div>

          <WrapperQC direction="column" gap="var(--spacing-5)">
            <p class="heading-5 text-center">
              {{ $t('travel.common.get_in_touch.request_callback') }}
            </p>

            <PhoneInputQC
              v-model="maskedPhone"
              v-model:country-iso.uppercase="countryCode"
              :errors="errors.phoneCompleted"
              data-test="need-help-modal-phone-input"
              auto-position-dropdown
              @completed="phoneCompleted = $event"
              @focus="v$.phone.$reset()"
              @change="onPhoneChange"
            />

            <ButtonQC
              medium
              data-test="need-help-modal-send-callback-button"
              :loading="isCallbackSendingProgress"
              @click="sendCallback"
            >
              {{ $t('contact_us_popup.send.button') }}
            </ButtonQC>

            <FlowPrivacyPolicyReminderQC
              place="popup_contact_us"
              class="need-help-qc__reminder caption-medium"
              data-test="popup-privacy-policy-reminder"
            />
          </WrapperQC>
        </WrapperQC>

        <WrapperQC v-else direction="column" gap="var(--spacing-5)">
          <WrapperQC
            direction="column"
            gap="var(--spacing-5)"
            :padding="{
              blockEnd: 'var(--spacing-7)',
            }"
          >
            <div class="need-help-qc__icon-wrapper">
              <img :src="iconSrc" alt="icon" />
            </div>

            <h4 class="need-help-qc__second-title text-center">
              {{
                isError
                  ? $t('travel.common.server_error_popup.heading')
                  : $t('travel.common.callback_sent_popup.heading')
              }}
            </h4>

            <p class="text-regular text-center">
              {{
                isError
                  ? $t('travel.common.server_error_popup.subheading')
                  : $t('travel.common.callback_sent_popup.subheading')
              }}
            </p>
          </WrapperQC>

          <WrapperQC direction="column" gap="var(--spacing-4)">
            <ButtonQC
              v-if="isError"
              data-test="need-help-modal-try-again-button"
              :loading="isCallbackSendingProgress"
              @click="sendCallback"
            >
              {{ $t('qic_error_try_again') }}
            </ButtonQC>

            <ButtonQC data-test="need-help-modal-close-button" variant="outline" @click="onClose">
              {{ isError ? $t('inbound_travelers_delete_cancel_button') : $t('qic.close') }}
            </ButtonQC>
          </WrapperQC>
        </WrapperQC>
      </div>
    </template>
  </ModalQC>
</template>

<style lang="scss" scoped>
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';
  @use '@/shared/assets/styles/mixins';

  .need-help__reminder {
    :deep(.ds-cell__title) {
      @include typography.caption-medium;
    }

    :deep(.ds-base-link) {
      color: var(--colorTxtSrfAction);
    }
  }

  .need-help-qc__title {
    @include typography.heading-3;

    @include mixins.mobile-flow {
      @include typography.heading-4;
    }
  }

  .need-help-qc__second-title {
    @include typography.heading-3;

    @include mixins.mobile-flow {
      @include typography.heading-5;
    }
  }

  .need-help-qc__icon-wrapper {
    width: 120px;
    height: 120px;
    margin: 0 auto;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .need-help-qc__or-border {
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--spacing-4);
    padding-block: var(--spacing-5);
    color: var(--colorTxtSrfGhost);

    &::before,
    &::after {
      content: '';
      display: flex;
      width: 100%;
      height: 1px;
      background-color: var(--colorBrdrSrfTertiary);
    }

    @include typography.footnote-medium;
  }
</style>
